// noinspection GraphQLUnresolvedReference

import { useMutation } from '@tanstack/react-query'

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import type { MutationOpts } from '../../../../base/types/reactQuery.ts'
import { EntidadInputProps } from '../../../../interfaces'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraFragmentAst } from '../fragments/impresoraFragment.ts'
import { ImpresoraProps } from '../interfaces/impresora.ts'
import { ApiImpresoraInputProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraFragmentAst}

  mutation IMPRESORA_REGISTRO($entidad: EntidadParamsInput!, $input: ImpresoraInput!) {
    impresoraRegistro(entidad: $entidad, input: $input) {
      ...impresoraFields
    }
  }
`

export const MUTATION_KEY_IMPRESORA_REGISTRO = ['impresora-registro'] as const

export interface ApiImpresoraRegistroParams {
  entidad: EntidadInputProps
  input: ApiImpresoraInputProps
}

/**
 * Servicio para registrar una impresora en la entidad indicada.
 * @param params Entidad (sucursal / punto de venta) y datos de la impresora.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiImpresoraRegistro = async (
  params: ApiImpresoraRegistroParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraProps> => {
  try {
    const data = await graphqlClient.request<{ impresoraRegistro: ImpresoraProps }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraRegistro
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Hook de mutación para registrar una impresora.
 * Para refrescar el listado al terminar: `onSuccess: () => invalidateImpresoraListado()`.
 * @param options Opciones de React Query (onSuccess, onError, ...).
 * @author isi-template
 */
export const useImpresoraRegistro = (options?: MutationOpts<ImpresoraProps, ApiImpresoraRegistroParams>) => {
  return useMutation({
    mutationKey: MUTATION_KEY_IMPRESORA_REGISTRO,
    ...options,
    mutationFn: (params: ApiImpresoraRegistroParams) => apiImpresoraRegistro(params),
  })
}
